import {
    createDisposer,
    globalRef,
    logDebug,
    type Maybe,
    throttle,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { onConnect } from '@amazon/vinyl-tsx'
import { windowEvents } from '@/util/interaction'
import { prefersReducedMotion } from '@/util/reducedMotion'
import { type ObservableValue, externalData } from '@amazon/vinyl-observable'
import { ctx } from '@/util/context'
import { afterNextPaint } from '@/util/timeSlice'

const { target } = ctx('domUtil')

/**
 * The percents an element must be towards the center before scrolling should be skipped.
 */
const CENTER_THRESHOLD_X = 0.1
const CENTER_THRESHOLD_Y = 0.1

/**
 * Focuses an element, preventing scrolling (rely on the scrollIntoViewOnFocus binding)
 */
export function focus(el: Maybe<HTMLElement>) {
    if (!el) return
    el.focus({ preventScroll: true })
}

export function scrollIntoViewIfNeeded(el: HTMLElement) {
    const rect = el.getBoundingClientRect()
    const vW = window.innerWidth
    const vH = window.innerHeight
    const marginW = CENTER_THRESHOLD_X * vW
    const marginH = CENTER_THRESHOLD_Y * vH
    const scrollX = rect.left < marginW || rect.right > vW - marginW
    const scrollY = rect.top < marginH || rect.bottom > vH - marginH

    if (scrollX || scrollY) {
        el.scrollIntoView({
            block: scrollY ? 'center' : 'nearest',
            inline: scrollX ? 'center' : 'nearest',
            // Honor the user's motion preference — under
            // `prefers-reduced-motion: reduce` we jump instead of animating.
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        })
    }
}

export function scrollIntoViewOnFocus(): Unsubscribe {
    const { add, dispose } = createDisposer()

    let lastInteraction: 'keyboard' | 'pointer' | null = null

    add(
        windowEvents.on(
            'keydown',
            () => {
                lastInteraction = 'keyboard'
            },
            { capture: true }
        )
    )

    add(
        windowEvents.on(
            'mousedown',
            () => {
                lastInteraction = 'pointer'
            },
            { capture: true }
        )
    )

    add(
        windowEvents.on(
            'pointerdown',
            () => {
                lastInteraction = 'pointer'
            },
            { capture: true }
        )
    )

    // Reset on window blur so the synthetic `focusin` the browser fires when
    // the window regains focus (alt-tab back) doesn't inherit the stale
    // 'keyboard' interaction — the Alt keydown that switched windows would
    // otherwise make us scroll the restored focus target into view.
    add(
        windowEvents.on('blur', () => {
            lastInteraction = null
        })
    )

    let scrollTarget: HTMLElement | null = null
    const throttledScrollInfoView = throttle(
        () => {
            if (scrollTarget) {
                logDebug(target, 'scroll: focus target→into view (keyboard focus)')
                scrollIntoViewIfNeeded(scrollTarget)
                scrollTarget = null
            }
        },
        0.3,
        { leading: true, trailing: true }
    )
    add(
        windowEvents.on('focusin', (event) => {
            const focused = event.target as HTMLElement | null
            if (focused && lastInteraction === 'keyboard' && !focused.closest('[role="menu"]')) {
                scrollTarget = focused
                throttledScrollInfoView()
            }
        })
    )
    return dispose
}

/**
 * The single element that scrolls the page. The app shell puts the page scroll
 * on `#main-content` (a full-viewport `overflow-y: auto` container) rather than
 * the document, so a side pane can scroll independently beside it. Every
 * page-level scroll read/write resolves the scroller through here (or via
 * `getScrollParent`, which finds the same element by walking up), so nothing
 * hardcodes `document.scrollingElement`/`window.scroll*`.
 *
 * Falls back to `document.scrollingElement` when the container isn't mounted
 * (SSR / static prerender / unit tests) so callers always get a scrollable.
 */
export function getAppScroller(): HTMLElement | null {
    return (
        (document.getElementById('main-content') as HTMLElement | null) ??
        (document.scrollingElement as HTMLElement | null)
    )
}

/**
 * Scrolls the page to top, instantly.
 */
export function scrollToTop() {
    getAppScroller()?.scroll({
        top: 0,
        left: 0,
        behavior: 'instant',
    })
}

/**
 * The element an IntersectionObserver on `el` measures against: its nearest
 * vertical scroll container (`#main-content` for page content). An implicit
 * viewport root is clipped by that container, so its `rootMargin` would never
 * reach past the container's visible area.
 *
 * A horizontal rail (whose `overflow-y` computes to `auto` beside its
 * `overflow-x`) is not a vertical scroller, and an ambiguous two-axis scroller is
 * passed over for the next one up. A fixed-position box, or one outside every
 * scroll container, measures against the viewport (null).
 */
export function getIntersectionRoot(
    el: Element,
    scrollBoxes = new Map<Element, ScrollBox>()
): Element | null {
    for (let node: Element | null = el; node; node = node.parentElement) {
        const box = scrollBoxOf(node, scrollBoxes)
        if (node !== el && box.verticalScroller) return node
        if (box.fixed) return null
    }
    return null
}

interface ScrollBox {
    readonly verticalScroller: boolean
    readonly fixed: boolean
}

function scrollBoxOf(node: Element, scrollBoxes: Map<Element, ScrollBox>): ScrollBox {
    let box = scrollBoxes.get(node)
    if (!box) {
        const style = getComputedStyle(node)
        box = {
            verticalScroller:
                overflowAllowsScroll(style.overflowY) && !overflowAllowsScroll(style.overflowX),
            fixed: style.position === 'fixed',
        }
        scrollBoxes.set(node, box)
    }
    return box
}

/**
 * Observes `element`'s intersection with {@link getIntersectionRoot} (unless
 * `init.root` is given, null included), from when it is connected until it
 * disconnects or the returned function is called.
 */
export function observeIntersection(
    element: Element,
    init: IntersectionObserverInit,
    callback: IntersectionObserverCallback
): Unsubscribe {
    return onConnect(element, () => {
        let observer: IntersectionObserver | null = null
        let connected = true
        const observe = (root: Element | Document | null) => {
            if (!connected) return
            observer = new IntersectionObserver(callback, { ...init, root })
            observer.observe(element)
        }
        if (init.root === undefined) resolveIntersectionRoot(element, observe)
        else observe(init.root)
        return () => {
            connected = false
            observer?.disconnect()
        }
    })
}

type PendingRoot = readonly [Element, (root: Element | null) => void]

const pendingRootsRef = globalRef<PendingRoot[]>(() => [])

// Resolved after the next paint, not in the connect pass: a style read there forces the style of
// the page just mounted inside the task that mounted it, where the frame computes it anyway.
function resolveIntersectionRoot(element: Element, resolve: (root: Element | null) => void) {
    const pendingRoots = pendingRootsRef.value
    if (pendingRoots.length === 0) {
        afterNextPaint(() => {
            const scrollBoxes = new Map<Element, ScrollBox>()
            for (const [el, resolveRoot] of pendingRoots.splice(0)) {
                resolveRoot(getIntersectionRoot(el, scrollBoxes))
            }
        })
    }
    pendingRoots.push([element, resolve])
}

export function isIntersectingProvider(
    element: HTMLElement,
    init: IntersectionObserverInit
): ObservableValue<boolean> {
    return externalData(false, (setData) =>
        observeIntersection(element, init, (entries) => {
            for (const entry of entries) setData(entry.isIntersecting)
        })
    )
}

/**
 * Given an element, returns the nearest parent with scroll overflow behavior.
 */
export function getScrollParent(
    el: HTMLElement,
    direction: 'vertical' | 'horizontal' = 'vertical'
): Element | null {
    let parent = el.parentElement
    while (parent) {
        const style = getComputedStyle(parent)
        const canScroll = overflowAllowsScroll(
            direction === 'horizontal' ? style.overflowX : style.overflowY
        )
        if (canScroll) return parent
        parent = parent.parentElement
    }
    // No overflow-scroll ancestor found: the page scroller is `#main-content`
    // (falls back to scrollingElement when it isn't mounted). For any element
    // mounted inside the shell this is the same container the walk above would
    // reach, so drag auto-scroll and windowing target the element that moves.
    return getAppScroller()
}

function overflowAllowsScroll(overflow: string): boolean {
    return overflow === 'auto' || overflow === 'scroll'
}

export function getViewportRect(): DOMRect {
    return {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
        width: window.innerWidth,
        height: window.innerHeight,
        x: 0,
        y: 0,
        toJSON: () => null,
    }
}

/**
 * Converts a viewport-relative DOMRect to coordinates relative to a given DOMRect base.
 *
 * @param rect - The DOMRect to convert (e.g. from getBoundingClientRect).
 * @param base - The DOMRect to convert relative to (usually from relativeTo.getBoundingClientRect()).
 * @returns A DOMRect-like object with coordinates relative to the base rect.
 */
export function toLocalRect(rect: DOMRect, base: DOMRect): DOMRect {
    return {
        x: rect.x - base.x,
        y: rect.y - base.y,
        left: rect.left - base.left,
        top: rect.top - base.top,
        right: rect.right - base.left,
        bottom: rect.bottom - base.top,
        width: rect.width,
        height: rect.height,
        toJSON: () => null,
    }
}

/**
 * Effective page `zoom` factor applied to `<body>` descendants.
 *
 * Below 320px `styles.scss` sets `body { zoom: min(1, …) }` (and
 * `html.smarttv` sets its own `zoom`) to shrink the whole page. CSS
 * `zoom` scales an element's *rendered* size but pointer/touch
 * coordinates (`clientX/Y`, `getBoundingClientRect`) stay in the
 * UNSCALED screen space. So a value derived from those coords and then
 * written back into a zoomed element's own space — a `transform:
 * translate`, or a `fixed` element's `top`/`left` — renders at
 * `value * zoom` and drifts from the finger whenever zoom ≠ 1.
 *
 * Divide such a screen-space delta by this factor before writing it into
 * a body-descendant's local space so it renders 1:1 with the pointer.
 * The factor is the product of every zoom on the ancestor chain we
 * control (`html` × `body`); a missing / unset `zoom` parses to `1`.
 */
export function pageZoom(): number {
    if (typeof window === 'undefined') return 1
    // `zoom` isn't in the DOM typings, so read it via `getPropertyValue`.
    const htmlZoom =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('zoom')) || 1
    const bodyZoom = parseFloat(getComputedStyle(document.body).getPropertyValue('zoom')) || 1
    return htmlZoom * bodyZoom
}

/**
 * Creates a ResizeObserver, watching the specified element.
 * When the returned Unsubscribe is called, the observer will disconnect.
 *
 * @param element
 * @param callback
 */
export function onResize(element: HTMLElement, callback: () => void): Unsubscribe {
    const resizeObserver = new ResizeObserver(callback)
    resizeObserver.observe(element)
    callback()
    return () => {
        resizeObserver.disconnect()
    }
}

/** Reads layout, returning the DOM writes that act on what it read. */
export type LayoutMeasure = () => (() => void) | void

interface MeasuredResizes {
    readonly measures: Map<Element, LayoutMeasure>
    readonly observer: ResizeObserver
}

const measuredResizesRef = globalRef((): MeasuredResizes => {
    const measures = new Map<Element, LayoutMeasure>()
    const observer = new ResizeObserver((entries) => {
        const writes: (() => void)[] = []
        for (const { target } of entries) {
            const write = measures.get(target)?.()
            if (write) writes.push(write)
        }
        for (const write of writes) write()
    })
    return { measures, observer }
})

/**
 * Like {@link onResize}, but `measure` first runs at the observer's first delivery after layout
 * rather than at the call, and one shared observer runs every element's reads before any of
 * their writes. A page of N measured elements then lays out once, where reading in the connect
 * pass, between other elements' writes, laid it out N times.
 */
export function onResizeMeasured(element: Element, measure: LayoutMeasure): Unsubscribe {
    const { measures, observer } = measuredResizesRef.value
    measures.set(element, measure)
    observer.observe(element)
    return () => {
        measures.delete(element)
        observer.unobserve(element)
    }
}

/**
 * Creates an IntersectionObserver on [element] that invokes [callback] whenever
 * its intersection with the viewport changes. Complements {@link onResize}: it
 * catches the element being shifted into or out of view by a SIBLING layout
 * change (async content above it collapsing or loading) — a move that changes
 * the element's position, not its size, and fires no scroll or resize event.
 */
export function onIntersectionChange(element: Element, callback: () => void): Unsubscribe {
    const observer = new IntersectionObserver(callback)
    observer.observe(element)
    return () => observer.disconnect()
}
